import React, { FormEvent, PropsWithChildren, Ref, useCallback } from 'react';
import { APIClient, Method, RequestOptions } from '../../utils/APIClient';
import { ErrorCapturer } from '../../utils/errorHandling';
import { serializeFormJSON } from '../../utils/form-serialization';

class FormUsageError extends Error {
  public override readonly name = 'FormUsageError';
  public constructor(
    msg: string,
    public readonly form: HTMLFormElement,
  ) {
    super(msg);
  }
}

export interface PreSubmitData<P> {
  requestOptions: RequestOptions<P>;
  inputs: P;
}

interface JSONFormProps<T, P extends object | undefined = undefined> {
  readonly method: Method;
  readonly path: string;
  readonly id?: string;
  readonly formRef?: Ref<HTMLFormElement>;
  /**
   * When specified, overrides value from form.
   */
  readonly onSuccess: (data: T) => void;
  readonly onFailure?: (err: Error) => boolean;
  readonly preSubmit?: (reqOpts: PreSubmitData<P>) => void;
  readonly onChange?: (ev: FormEvent<HTMLElement>) => void;
}

let api: APIClient;
let capturer: ErrorCapturer;

export function init(initApi: APIClient, initCapturer: ErrorCapturer): void {
  api = initApi;
  capturer = initCapturer;
}

export const JSONForm = <
  T extends object | undefined = undefined,
  P extends object | undefined = undefined,
>({
  method,
  path,
  preSubmit,
  onSuccess,
  onFailure,
  formRef,
  id,
  onChange,
  children,
}: PropsWithChildren<JSONFormProps<T, P>>): React.JSX.Element => {
  const onSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
      const form = event.currentTarget as HTMLFormElement;
      event.preventDefault();

      if (method.toLocaleLowerCase() !== method) {
        throw new FormUsageError(`Malformed form method '${method}'`, form);
      }

      if (!form.action) {
        throw new FormUsageError('Form action not set', form);
      }

      const inputs = Array.from(
        form.querySelectorAll<HTMLInputElement>('textarea, input, select, button'),
      );

      for (const input of inputs) {
        if (input.type === 'submit') {
          input.classList.add('active');
        }

        input.disabled = true;
      }

      try {
        const headers = new Headers();
        const payload = serializeFormJSON(form) as P;
        const requestOptions = {
          headers,
          method,
          payload: method === 'delete' ? undefined : payload,
          url: path,
        };

        if (preSubmit) {
          preSubmit({
            inputs: payload,
            requestOptions,
          });
        }

        const res = await api.request<T>(requestOptions);
        onSuccess(res);
      } catch (error) {
        if (onFailure?.(error as Error)) {
          return;
        }

        capturer(error as Error);
      } finally {
        for (const input of inputs) {
          if (input.type === 'submit') {
            input.classList.remove('active');
          }

          input.disabled = false;
        }
      }
    },
    [method, path, preSubmit, onSuccess, onFailure],
  );

  return (
    <form
      action={path}
      id={id || ''}
      method="post"
      onChange={onChange}
      onSubmit={onSubmit}
      ref={formRef}
    >
      {children}
    </form>
  );
};
